import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';

export type DevConsoleTab = 'sandbox';

export interface UIState {
  activeModal: string | null;
  inspectorOpen: boolean;
  devConsoleOpen: boolean;
  devConsoleTab: DevConsoleTab;
  /** FRONT-252: the Policy page's "How this works" note. Open until the
   *  person closes it; their choice is remembered. */
  policyExplainerOpen: boolean;
}

export interface UIActions {
  openModal: (modalId: string) => void;
  closeModal: () => void;
  toggleInspector: () => void;
  setInspectorOpen: (open: boolean) => void;
  toggleDevConsole: () => void;
  setDevConsoleTab: (tab: DevConsoleTab) => void;
  setPolicyExplainerOpen: (open: boolean) => void;
}

export type UIStore = UIState & UIActions;

const initialState: UIState = {
  activeModal: null,
  // The Inspector starts CLOSED for a user who has never touched it. It is a
  // diagnostic side panel, not part of the primary reading flow, and opening it
  // by default spent a third of the width on information most turns don't need
  // — the chat is what the user came for.
  //
  // Nothing is hidden by this: the header's Inspector button turns amber with a
  // pulsing dot whenever something actually wants attention (a pending
  // approval, an active run), so the panel advertises itself when it matters
  // and stays out of the way when it doesn't.
  //
  // Once the user opens or closes it, that choice is persisted (see
  // UI_STORE_KEY) and wins over this default on every later load.
  inspectorOpen: false,
  devConsoleOpen: false,
  devConsoleTab: 'sandbox',
  policyExplainerOpen: true,
};

/** localStorage key for the persisted slice below. */
export const UI_STORE_KEY = 'eddy.ui';

/**
 * FRONT-1187: only `inspectorOpen` is persisted, deliberately (plus, since
 * FRONT-252, `policyExplainerOpen` — the same kind of device preference).
 *
 * - `activeModal` must NOT be — restoring a modal that happened to be open when
 *   the tab closed would greet the user with a dialog they never asked for.
 * - `devConsoleOpen` must NOT be — a debugging panel reappearing days later,
 *   with no memory of opening it, reads as a bug.
 * - `devConsoleTab` could be, but it only matters while the console is open, so
 *   persisting it buys nothing.
 *
 * Not registered in `reconcileAccountScope`'s purge (FRONT-732): that exists for
 * per-account DATA leaking between users on a shared browser. Whether a side
 * panel is open is a device preference, like the sidebar layout — carrying it
 * across a login is the desirable behaviour, not a leak.
 */
export function createUIStore(): StoreApi<UIStore> {
  return createStore<UIStore>(
    'ui-store',
    (set) => ({
      ...initialState,
      openModal: (modalId) => set({ activeModal: modalId }),
      closeModal: () => set({ activeModal: null }),
      toggleInspector: () => set((state) => ({ inspectorOpen: !state.inspectorOpen })),
      setInspectorOpen: (open) => set({ inspectorOpen: open }),
      toggleDevConsole: () => set((state) => ({ devConsoleOpen: !state.devConsoleOpen })),
      setDevConsoleTab: (tab) => set({ devConsoleTab: tab }),
      setPolicyExplainerOpen: (open) => set({ policyExplainerOpen: open }),
    }),
    {
      key: UI_STORE_KEY,
      version: 1,
      partialize: (state) => ({
        inspectorOpen: state.inspectorOpen,
        policyExplainerOpen: state.policyExplainerOpen,
      }),
    },
  );
}

let store: StoreApi<UIStore> | undefined;

export function getUIStore(): StoreApi<UIStore> {
  if (!store) store = createUIStore();
  return store;
}

if (import.meta.hot) {
  import.meta.hot.accept(() => { store = undefined; });
}

export function useUIStore<T>(selector: (state: UIStore) => T): T {
  return useStore(getUIStore(), selector);
}

export function useModal() {
  const activeModal = useUIStore((s) => s.activeModal);
  const open = useUIStore((s) => s.openModal);
  const close = useUIStore((s) => s.closeModal);
  const isOpen = (id: string) => activeModal === id;
  return { activeModal, open, close, isOpen };
}

export function useInspector() {
  const open = useUIStore((s) => s.inspectorOpen);
  const toggle = useUIStore((s) => s.toggleInspector);
  const setOpen = useUIStore((s) => s.setInspectorOpen);
  return { open, toggle, setOpen };
}

export function useDevConsole() {
  const open = useUIStore((s) => s.devConsoleOpen);
  const tab = useUIStore((s) => s.devConsoleTab);
  const toggle = useUIStore((s) => s.toggleDevConsole);
  const setTab = useUIStore((s) => s.setDevConsoleTab);
  return { open, tab, toggle, setTab };
}
